import * as React from 'react';
import { ReactNode, useCallback, useRef } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, IconButton, useSlug } from '@bloomberg/media-ui-core';
import { Exit } from '@bloomberg/media-ui-icons';
import type {
  FloatingPortalProps,
  Placement,
  Strategy,
} from '@floating-ui/react';
import {
  arrow,
  autoPlacement,
  autoUpdate,
  FloatingArrow,
  FloatingFocusManager,
  FloatingPortal,
  hide,
  offset,
  shift,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';

import styles from './Popover.module.css';

export type PopoverProps = GlobalProps & {
  Icon?: ReactNode;
  eyebrow: string;
  badgeText?: string;
  text?: ReactNode;
  onDismiss?: () => void;
  anchorElement: Element | null;
  allowedPlacements?: Placement[];
  portalRoot?: FloatingPortalProps['root'];
  portalId?: FloatingPortalProps['id'];
  floatingStrategy?: Strategy;
  acceptZeroDimensionAnchors?: boolean;
};

/**
 * When testing, pass in acceptZeroDimensionAnchors so it can render in a dimensionless environment such as jest-dom
 */
export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(
  function PopoverComponent(props, ref) {
    const {
      Icon,
      anchorElement,
      onDismiss,
      allowedPlacements,
      eyebrow,
      name = 'Popover',
      text,
      portalRoot,
      badgeText,
      portalId,
      unstyled,
      floatingStrategy,
      acceptZeroDimensionAnchors = false,
    } = props;

    const componentName = useSlug(name);
    const [open, setOpen] = React.useState(true);

    const arrowRef = useRef(null);
    const { refs, floatingStyles, context, middlewareData, update } =
      useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        strategy: floatingStrategy,
        elements: {
          reference: anchorElement,
        },
        middleware: [
          offset(20),
          autoPlacement({
            crossAxis: true,
            allowedPlacements: allowedPlacements ?? [
              'left',
              'right',
              'top-start',
              'top-end',
              'bottom-start',
              'bottom-end',
            ],
            padding: 5,
          }),
          hide({ padding: 5 }),
          shift({ padding: 5 }),
          arrow({
            element: arrowRef,
            padding: 30,
          }),
        ],
      });
    const role = useRole(context);
    const { getFloatingProps } = useInteractions([role]);
    const anchorIsHidden = middlewareData.hide?.referenceHidden;
    const anchorHasNoSize =
      anchorElement?.getBoundingClientRect().width === 0 &&
      anchorElement?.getBoundingClientRect().height === 0;
    const { isMounted, styles: transitionStyles } = useTransitionStyles(
      context,
      {
        duration: 300,
      }
    );

    if (refs.reference.current !== anchorElement) {
      refs.setReference(anchorElement);
      update();
    }

    const cx = classes(
      [styles.wrapper, anchorIsHidden && styles.invisibleWrapper],
      {
        ...props,
        unstyled,
        name,
      }
    );

    const setRefs = useMergeRefs([refs.setFloating, ref]);

    const onClose = useCallback(() => {
      setOpen(false);

      if (onDismiss) {
        onDismiss();
      }
    }, []);

    if (!isMounted || (!acceptZeroDimensionAnchors && anchorHasNoSize))
      return null;

    return (
      <FloatingPortal root={portalRoot} id={portalId}>
        <FloatingFocusManager
          initialFocus={-1}
          returnFocus={false}
          closeOnFocusOut={false}
          modal={false}
          context={context}
        >
          <div
            className={cx}
            ref={setRefs}
            data-component={componentName}
            style={{ ...floatingStyles, ...transitionStyles }}
            {...getFloatingProps()}
          >
            <FloatingArrow
              style={{ transform: 'translateY(-1px)' }}
              className={styles.arrow}
              height={18}
              width={22}
              ref={arrowRef}
              context={context}
              strokeWidth={1}
              stroke="var(--color-gray-700)"
            />
            <p className={styles.eyebrow}>
              <span className={styles.eyebrowText}>
                {badgeText && <span className={styles.badge}>{badgeText}</span>}
                {Icon}
                {eyebrow}
              </span>
              <IconButton
                onClick={onClose}
                size="small"
                version="phoenix"
                Icon={<Exit className={styles.exitIcon} />}
              />
            </p>
            {text && <p>{text}</p>}
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  }
);
